import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ShieldCheck, Download, Search, AlertTriangle, CheckCircle2, Info, Wand2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_SAMPLE = `Godown Rent
Factory Milk & Tea
Hamali Charges
Coolie Charges
Security Charges
Repair & Maintenance
Printing & Stationery
Postage & Courier`;

function fmt(n) {
  if (n === null || n === undefined || n === "") return "—";
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(n);
}

function statusBadge(r) {
  if (r.voucher_count === 0 && !r.in_trial_balance && !r.in_ledger_master) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">
        <AlertTriangle className="w-3 h-3" /> NOT FOUND
      </span>
    );
  }
  if (r.voucher_count === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
        <Info className="w-3 h-3" /> NO VOUCHERS
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
      <CheckCircle2 className="w-3 h-3" /> FOUND
    </span>
  );
}

export default function LedgerAudit({ companyContext }) {
  const [input, setInput] = useState(DEFAULT_SAMPLE);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [results, setResults] = useState([]);
  const [expanded, setExpanded] = useState(null);

  const parseLedgers = () =>
    input.split("\n").map((s) => s.trim()).filter(Boolean);

  const buildPayload = () => ({
    ledgers: parseLedgers(),
    company_id: companyContext?.companyId || "default",
    branch_id: companyContext?.branchId || null,
    from_date: fromDate || null,
    to_date: toDate || null,
    fuzzy: true,
  });

  const runAudit = async () => {
    const ledgers = parseLedgers();
    if (ledgers.length === 0) {
      toast.error("Paste at least one ledger name");
      return;
    }
    setLoading(true);
    setExpanded(null);
    try {
      const res = await axios.post(`${API}/diagnostics/ledger-audit`, buildPayload());
      setResults(res.data.results || []);
      toast.success(`Audited ${res.data.results.length} ledgers`);
    } catch (e) {
      toast.error("Audit failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = async () => {
    const ledgers = parseLedgers();
    if (ledgers.length === 0) {
      toast.error("Paste at least one ledger name");
      return;
    }
    setExporting(true);
    try {
      const res = await axios.post(
        `${API}/diagnostics/ledger-audit/export`,
        buildPayload(),
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Ledger_Audit_${new Date().toISOString().slice(0, 10)}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (e) {
      toast.error("Export failed: " + e.message);
    } finally {
      setExporting(false);
    }
  };

  const autoDetectGaps = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/diagnostics/pl-ledgers-without-vouchers`, {
        params: {
          company_id: companyContext?.companyId || "default",
          branch_id: companyContext?.branchId || undefined,
        },
      });
      const rows = res.data.rows || [];
      if (rows.length === 0) {
        toast.info("No gaps found — every P&L ledger with TB closing has voucher activity.");
        return;
      }
      const names = rows.map((r) => r.name).join("\n");
      setInput(names);
      toast.success(`Auto-loaded ${rows.length} P&L ledgers with TB closing but zero vouchers`);
      // immediately run audit with the freshly loaded names
      const payload = {
        ledgers: rows.map((r) => r.name),
        company_id: companyContext?.companyId || "default",
        branch_id: companyContext?.branchId || null,
        from_date: fromDate || null,
        to_date: toDate || null,
        fuzzy: true,
      };
      const auditRes = await axios.post(`${API}/diagnostics/ledger-audit`, payload);
      setResults(auditRes.data.results || []);
    } catch (e) {
      toast.error("Auto-detect failed: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const totals = results.reduce(
    (a, r) => {
      if (r.voucher_count === 0 && !r.in_trial_balance && !r.in_ledger_master) a.missing += 1;
      else if (r.voucher_count === 0) a.tbOnly += 1;
      else a.found += 1;
      return a;
    },
    { found: 0, tbOnly: 0, missing: 0 }
  );

  return (
    <div className="space-y-6" data-testid="ledger-audit-view">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-zinc-900"
              style={{ fontFamily: "'Chivo', system-ui, sans-serif" }}>
            Ledger Audit
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Paste a list of ledger names to check where they exist — Ledger Master, Trial Balance, or Vouchers.
            Useful for reconciling MIS numbers against an external P&L.
          </p>
        </div>
        <ShieldCheck className="w-10 h-10 text-zinc-300" />
      </div>

      <Card className="border-zinc-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-bold text-zinc-900">Input</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide text-zinc-500">
              Ledger Names (one per line)
            </label>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              rows={8}
              data-testid="ledger-audit-input"
              className="mt-1 w-full text-sm font-mono border border-zinc-300 rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent resize-y"
              placeholder="e.g.&#10;Godown Rent&#10;Factory Milk & Tea&#10;Hamali Charges"
              style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}
            />
          </div>

          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wide text-zinc-500">
                From Date
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                data-testid="ledger-audit-from"
                className="mt-1 block text-sm border border-zinc-300 rounded-md px-3 py-1.5"
              />
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wide text-zinc-500">
                To Date
              </label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                data-testid="ledger-audit-to"
                className="mt-1 block text-sm border border-zinc-300 rounded-md px-3 py-1.5"
              />
            </div>
            <div className="flex gap-2 ml-auto">
              <Button
                onClick={autoDetectGaps}
                disabled={loading}
                variant="outline"
                data-testid="ledger-audit-auto"
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
              >
                <Wand2 className="w-4 h-4 mr-2" />
                Auto-detect Gaps
              </Button>
              <Button
                onClick={runAudit}
                disabled={loading}
                data-testid="ledger-audit-run"
                className="bg-zinc-900 hover:bg-zinc-800 text-white"
              >
                <Search className="w-4 h-4 mr-2" />
                {loading ? "Auditing..." : "Run Audit"}
              </Button>
              <Button
                onClick={downloadExcel}
                disabled={exporting}
                variant="outline"
                data-testid="ledger-audit-export"
                className="border-zinc-300"
              >
                <Download className="w-4 h-4 mr-2" />
                {exporting ? "Exporting..." : "Download Excel"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {results.length > 0 && (
        <>
          <div className="grid grid-cols-3 gap-3" data-testid="ledger-audit-summary">
            <StatCard label="Found in Vouchers" value={totals.found} color="emerald" />
            <StatCard label="TB / Master Only" value={totals.tbOnly} color="amber" />
            <StatCard label="Not Found Anywhere" value={totals.missing} color="rose" />
          </div>

          <Card className="border-zinc-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-bold text-zinc-900">
                Results ({results.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-zinc-50 hover:bg-zinc-50">
                    <TableHead className="text-[11px] font-bold uppercase tracking-wide text-zinc-600">Query</TableHead>
                    <TableHead className="text-[11px] font-bold uppercase tracking-wide text-zinc-600">Status</TableHead>
                    <TableHead className="text-[11px] font-bold uppercase tracking-wide text-zinc-600">In Master</TableHead>
                    <TableHead className="text-[11px] font-bold uppercase tracking-wide text-zinc-600">In TB</TableHead>
                    <TableHead className="text-right text-[11px] font-bold uppercase tracking-wide text-zinc-600">TB Closing</TableHead>
                    <TableHead className="text-right text-[11px] font-bold uppercase tracking-wide text-zinc-600">Vouchers</TableHead>
                    <TableHead className="text-right text-[11px] font-bold uppercase tracking-wide text-zinc-600">Debit</TableHead>
                    <TableHead className="text-right text-[11px] font-bold uppercase tracking-wide text-zinc-600">Credit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {results.map((r) => (
                    <RowGroup
                      key={r.query}
                      row={r}
                      isOpen={expanded === r.query}
                      onToggle={() =>
                        setExpanded(expanded === r.query ? null : r.query)
                      }
                    />
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, color }) {
  const map = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    rose: "border-rose-200 bg-rose-50 text-rose-700",
  };
  return (
    <div className={`rounded-lg border p-4 ${map[color]}`} data-testid={`stat-${color}`}>
      <div className="text-[11px] font-bold uppercase tracking-wide opacity-80">{label}</div>
      <div className="text-3xl font-black tabular-nums mt-1"
           style={{ fontFamily: "'Chivo', system-ui, sans-serif" }}>
        {value}
      </div>
    </div>
  );
}

function RowGroup({ row, isOpen, onToggle }) {
  const hasRows = (row.rows || []).length > 0;
  return (
    <>
      <TableRow
        className={`hover:bg-zinc-50 ${hasRows ? "cursor-pointer" : ""}`}
        onClick={hasRows ? onToggle : undefined}
        data-testid={`audit-row-${row.query}`}
      >
        <TableCell className="font-semibold text-zinc-900">
          {row.query}
          {row.tb_matched_name && row.tb_matched_name !== row.query && (
            <span className="text-[11px] text-zinc-500 ml-2">→ {row.tb_matched_name}</span>
          )}
        </TableCell>
        <TableCell>{statusBadge(row)}</TableCell>
        <TableCell className="text-xs">{row.in_ledger_master ? "Yes" : "—"}</TableCell>
        <TableCell className="text-xs">{row.in_trial_balance ? "Yes" : "—"}</TableCell>
        <TableCell className="text-right tabular-nums text-xs">{fmt(row.tb_closing)}</TableCell>
        <TableCell className="text-right tabular-nums font-bold">{row.voucher_count}</TableCell>
        <TableCell className="text-right tabular-nums text-xs">{fmt(row.total_debit)}</TableCell>
        <TableCell className="text-right tabular-nums text-xs">{fmt(row.total_credit)}</TableCell>
      </TableRow>
      {isOpen && hasRows && (
        <TableRow className="bg-blue-50/50 hover:bg-blue-50/50">
          <TableCell colSpan={8} className="p-0 border-l-2 border-blue-600">
            <div className="px-6 py-4">
              <div className="text-[11px] font-bold uppercase tracking-wide text-blue-700 mb-2">
                {row.rows.length} voucher line(s) for "{row.query}"
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-[10px]">Date</TableHead>
                    <TableHead className="text-[10px]">Type</TableHead>
                    <TableHead className="text-[10px]">Vch No</TableHead>
                    <TableHead className="text-[10px]">Party</TableHead>
                    <TableHead className="text-[10px]">Matched Ledger</TableHead>
                    <TableHead className="text-[10px] text-right">Debit</TableHead>
                    <TableHead className="text-[10px] text-right">Credit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {row.rows.slice(0, 200).map((ln, i) => {
                    const amt = Math.abs(ln.amount || 0);
                    const dt = (ln.date || "").split("T")[0];
                    return (
                      <TableRow key={`${ln.voucher_id}-${i}`} className="text-xs">
                        <TableCell className="tabular-nums">{dt}</TableCell>
                        <TableCell>{ln.type}</TableCell>
                        <TableCell className="tabular-nums">{ln.number}</TableCell>
                        <TableCell>{ln.party}</TableCell>
                        <TableCell className="text-zinc-600">{ln.ledger}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {ln.is_debit ? fmt(amt) : "—"}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {!ln.is_debit ? fmt(amt) : "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {row.rows.length > 200 && (
                <p className="text-[11px] text-zinc-500 mt-2">
                  Showing first 200 rows. Download Excel for the full list.
                </p>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
